import type { AppLanguage } from '../i18n/types';
import { localeTagForAppLanguage } from '../i18n/languages';
import { isCapacitorNative } from '../utils/isCapacitorNative';

export type SpeechRecognitionResultPayload = {
  transcript: string;
  interimTranscript: string;
  final: boolean;
};

export type SpeechRecognitionSessionHandlers = {
  onResult: (payload: SpeechRecognitionResultPayload) => void;
  onError: (message: string) => void;
  onEnd: () => void;
};

type BrowserSpeechRecognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
};

type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: ArrayLike<{
    isFinal: boolean;
    0: { transcript: string };
  }>;
};

type SpeechRecognitionCtor = new () => BrowserSpeechRecognition;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const w = window as Window & {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Cap for a tap-to-talk session (browser auto-stop and cloud recording). */
export const VOICE_LISTEN_MAX_MS = 8000;

/** True when the Web Speech API is available in this runtime. */
export function isBrowserSpeechRecognitionAvailable(): boolean {
  return getSpeechRecognitionCtor() !== null;
}

function microphoneDeniedError(): Error {
  const err = new Error('not-allowed');
  err.name = 'NotAllowedError';
  return err;
}

type NativeSpeechPlugin = {
  available: () => Promise<{ available: boolean }>;
  requestPermissions: () => Promise<{ speechRecognition?: string }>;
  start: (options: {
    language?: string;
    maxResults?: number;
    partialResults?: boolean;
    popup?: boolean;
  }) => Promise<{ matches?: string[] }>;
  stop: () => Promise<void>;
  addListener: (
    eventName: 'partialResults' | 'listeningState',
    listenerFunc: (data: { matches?: string[]; status?: 'started' | 'stopped' }) => void,
  ) => Promise<{ remove: () => Promise<void> }>;
};

let testNativePlugin: NativeSpeechPlugin | null = null;

/**
 * Wrapped in `{ plugin }`: a Capacitor plugin proxy answers every property,
 * including `then`, so resolving a promise with it directly never settles.
 */
async function loadNativeSpeechPlugin(): Promise<{ plugin: NativeSpeechPlugin }> {
  if (testNativePlugin) {
    return { plugin: testNativePlugin };
  }
  const mod = (await import('@capacitor-community/speech-recognition')) as {
    SpeechRecognition?: NativeSpeechPlugin;
    default?: { SpeechRecognition?: NativeSpeechPlugin };
  };
  const plugin = mod.SpeechRecognition ?? mod.default?.SpeechRecognition;
  if (!plugin) {
    throw new Error('unavailable');
  }
  return { plugin };
}

/** Test-only: avoid loading the Capacitor plugin in Jest. */
export function setNativeSpeechPluginForTests(plugin: NativeSpeechPlugin | null): void {
  testNativePlugin = plugin;
}

/** Permission dialogs wait for the user; the other native calls should return at once. */
const NATIVE_PERMISSION_TIMEOUT_MS = 60000;
const NATIVE_CALL_TIMEOUT_MS = 5000;

function withNativeTimeout<T>(step: string, promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`native-timeout-${step}`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/**
 * Ask once for the mic. On Capacitor this is the Android/iOS dialog.
 * Do not trust `permissions.query` in WebView — it often stays "denied"
 * for the hosted origin after the OS already granted RECORD_AUDIO.
 */
export async function ensureMicrophoneAccess(): Promise<void> {
  if (isCapacitorNative()) {
    const { plugin } = await withNativeTimeout('load', loadNativeSpeechPlugin(), NATIVE_CALL_TIMEOUT_MS);
    const status = await withNativeTimeout(
      'permission',
      plugin.requestPermissions(),
      NATIVE_PERMISSION_TIMEOUT_MS,
    );
    if (status?.speechRecognition !== 'granted') {
      throw microphoneDeniedError();
    }
    const availability = await withNativeTimeout('available', plugin.available(), NATIVE_CALL_TIMEOUT_MS);
    if (!availability?.available) {
      throw new Error('unavailable');
    }
    return;
  }
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw microphoneDeniedError();
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
  } catch (err) {
    const name = err instanceof Error ? err.name : '';
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
      throw microphoneDeniedError();
    }
    throw err;
  }
}

/**
 * Capacitor uses the native recognizer (one OS prompt). Browsers use Web Speech,
 * then cloud STT when `REACT_APP_VOICE_STT_URL` is set.
 */
export function resolveSpeechBackend(): 'native' | 'browser' | 'cloud' | 'unavailable' {
  if (isCapacitorNative()) {
    return 'native';
  }
  if (isBrowserSpeechRecognitionAvailable()) {
    return 'browser';
  }
  if (process.env.REACT_APP_VOICE_STT_URL?.trim()) {
    return 'cloud';
  }
  return 'unavailable';
}

export function speechRecognitionLanguageTag(language: AppLanguage): string {
  return localeTagForAppLanguage(language);
}

export type BrowserSpeechSession = {
  stop: () => void;
  abort: () => void;
};

export function startBrowserSpeechRecognition(
  language: AppLanguage,
  handlers: SpeechRecognitionSessionHandlers,
): BrowserSpeechSession | null {
  const Ctor = getSpeechRecognitionCtor();
  if (!Ctor) {
    return null;
  }
  const recognition = new Ctor();
  recognition.lang = speechRecognitionLanguageTag(language);
  recognition.continuous = false;
  recognition.interimResults = true;
  recognition.maxAlternatives = 1;

  recognition.onresult = (event) => {
    let interim = '';
    let finalText = '';
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const row = event.results[i];
      if (!row) {
        continue;
      }
      const piece = row[0]?.transcript ?? '';
      if (row.isFinal) {
        finalText += piece;
      } else {
        interim += piece;
      }
    }
    const transcript = (finalText || interim).trim();
    handlers.onResult({
      transcript,
      interimTranscript: interim.trim(),
      final: Boolean(finalText.trim()),
    });
  };

  recognition.onerror = (event) => {
    const code = event.error ?? 'unknown';
    if (code === 'aborted') {
      handlers.onEnd();
      return;
    }
    handlers.onError(code);
  };

  recognition.onend = () => {
    handlers.onEnd();
  };

  try {
    recognition.start();
  } catch {
    handlers.onError('start-failed');
    return null;
  }

  return {
    stop: () => {
      try {
        recognition.stop();
      } catch {
        /* already stopped */
      }
    },
    abort: () => {
      try {
        recognition.abort();
      } catch {
        /* already stopped */
      }
    },
  };
}

/** Maps Android SpeechRecognizer / plugin rejection text to a voice error code. */
export function nativeSpeechErrorCode(err: unknown): string {
  const message = (err instanceof Error ? err.message : typeof err === 'string' ? err : '').trim();
  if (message.startsWith('native-timeout-')) {
    return message;
  }
  if (/denied|permission|not allowed/i.test(message)) {
    return 'not-allowed';
  }
  if (/no match|no speech|didn.t understand/i.test(message)) {
    return 'no-speech';
  }
  if (/not available|unavailable|not implemented/i.test(message)) {
    return 'unavailable';
  }
  return message || 'start-failed';
}

/**
 * Android/iOS SpeechRecognizer — does not use Chrome site settings.
 * `partialResults: false` keeps the plugin call open until the recognizer
 * returns a result or an error; with `true` the call resolves immediately and
 * later recognizer errors are dropped.
 */
export async function startNativeSpeechRecognition(
  language: AppLanguage,
  handlers: SpeechRecognitionSessionHandlers,
): Promise<BrowserSpeechSession | null> {
  const { plugin } = await withNativeTimeout('load', loadNativeSpeechPlugin(), NATIVE_CALL_TIMEOUT_MS);
  let lastText = '';
  let finished = false;
  let partialHandle: { remove: () => Promise<void> } | null = null;

  const finish = (outcome: { text?: string; errorCode?: string }) => {
    if (finished) {
      return;
    }
    finished = true;
    void partialHandle?.remove().catch(() => undefined);
    partialHandle = null;
    if (outcome.errorCode) {
      handlers.onError(outcome.errorCode);
      return;
    }
    if (outcome.text) {
      handlers.onResult({ transcript: outcome.text, interimTranscript: '', final: true });
    }
    handlers.onEnd();
  };

  try {
    partialHandle = await withNativeTimeout(
      'listener',
      plugin.addListener('partialResults', (data) => {
        const text = data.matches?.[0]?.trim() ?? '';
        if (!text || finished) {
          return;
        }
        lastText = text;
        handlers.onResult({ transcript: text, interimTranscript: text, final: false });
      }),
      NATIVE_CALL_TIMEOUT_MS,
    );
  } catch {
    partialHandle = null;
  }

  plugin
    .start({
      language: speechRecognitionLanguageTag(language),
      maxResults: 1,
      partialResults: false,
      popup: false,
    })
    .then((result) => {
      finish({ text: result?.matches?.[0]?.trim() || lastText });
    })
    .catch((err) => {
      const code = nativeSpeechErrorCode(err);
      if (code === 'no-speech' && lastText) {
        finish({ text: lastText });
        return;
      }
      finish({ errorCode: code });
    });

  return {
    stop: () => {
      void plugin.stop().catch(() => undefined);
    },
    abort: () => {
      finish({});
      void plugin.stop().catch(() => undefined);
    },
  };
}

/** Record a short clip and POST it to the configured cloud STT endpoint. */
export async function transcribeWithCloudStt(
  language: AppLanguage,
  options?: { maxMs?: number; signal?: AbortSignal },
): Promise<string> {
  const endpoint = process.env.REACT_APP_VOICE_STT_URL?.trim();
  if (!endpoint) {
    throw new Error('cloud-stt-unconfigured');
  }
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw new Error('mic-unavailable');
  }

  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
    ? 'audio/webm;codecs=opus'
    : MediaRecorder.isTypeSupported('audio/webm')
      ? 'audio/webm'
      : '';
  const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
  const chunks: BlobPart[] = [];

  const stopped = new Promise<Blob>((resolve, reject) => {
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };
    recorder.onerror = () => reject(new Error('record-failed'));
    recorder.onstop = () => {
      resolve(new Blob(chunks, { type: recorder.mimeType || 'audio/webm' }));
    };
  });

  recorder.start();
  const maxMs = options?.maxMs ?? VOICE_LISTEN_MAX_MS;
  const timer = window.setTimeout(() => {
    if (recorder.state === 'recording') {
      recorder.stop();
    }
  }, maxMs);

  const onAbort = () => {
    window.clearTimeout(timer);
    if (recorder.state === 'recording') {
      recorder.stop();
    }
  };
  options?.signal?.addEventListener('abort', onAbort, { once: true });

  const blob = await stopped.finally(() => {
    window.clearTimeout(timer);
    options?.signal?.removeEventListener('abort', onAbort);
    stream.getTracks().forEach((track) => track.stop());
  });

  if (options?.signal?.aborted) {
    throw new Error('aborted');
  }

  const form = new FormData();
  form.append('audio', blob, 'utterance.webm');
  form.append('language', speechRecognitionLanguageTag(language));
  if (isCapacitorNative()) {
    form.append('client', 'capacitor');
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    body: form,
    signal: options?.signal,
  });
  if (!response.ok) {
    throw new Error(`cloud-stt-http-${response.status}`);
  }
  const json = (await response.json()) as { text?: unknown; transcript?: unknown };
  const text =
    typeof json.text === 'string'
      ? json.text.trim()
      : typeof json.transcript === 'string'
        ? json.transcript.trim()
        : '';
  if (!text) {
    throw new Error('cloud-stt-empty');
  }
  return text;
}
